import {
  faRotate,
  faTriangleExclamation
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog, { type DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import type { DeviceModel } from '@/features/DevicesTable';
import { DEFAULT_LOG_SOURCES_FOR_NEW_DEVICE } from '@/pages/Administration/Devices/Devices.constants';
import { minifyJson } from '@/pages/Administration/Devices/Devices.service';
import devicesAtom, {
  selectedDeviceEditDetailsAtom,
  selectedDeviceIdAtom
} from '@/pages/Administration/Devices/Devices.store';
import {
  CLOSE_REASON,
  type CloseHandler,
  type CloseReason,
  IGNORED_REASONS
} from '@/pages/Administration/Devices/Devices.types';
import { selectedOrganizationAtom } from '@/pages/Organizations';

import { useDeviceDialog } from '../DeviceCreate.hooks';
import type {
  DeviceCreateDTO,
  DeviceCreateFormModel
} from '../DeviceCreate.types';
import DeviceCreateForm from '../DeviceCreateForm';
import useStyles from './DeviceCreateDialog.styles';

const DeviceCreateDialog = () => {
  const formMethods = useForm<DeviceCreateFormModel>();
  const { handleSubmit, reset } = formMethods;
  const [deviceId] = useAtom(selectedDeviceIdAtom);
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);
  const [selectedDeviceEditDetails] = useAtom(selectedDeviceEditDetailsAtom);
  const [devices] = useAtom(devicesAtom);
  const {
    open: showDevicePopup,
    error,
    mutation,
    mode,
    closeDeviceDialog
  } = useDeviceDialog();
  const { classes } = useStyles({ mode });

  const [scroll] = useState<DialogProps['scroll']>('paper');

  const resetAndClose = () => {
    closeDeviceDialog();
    // reset form values
    reset();
  };

  const handleClose: CloseHandler = (_event: object, reason: CloseReason) => {
    if (IGNORED_REASONS.includes(reason)) {
      return;
    }
    resetAndClose();
  };

  const hostName =
    devices.find((device: DeviceModel) => device.device_uid === deviceId)
      ?.host_name || '';

  const title = useMemo(() => {
    let titleStr = '';
    if (mode === 'New') {
      titleStr = 'Create New Device';
    }
    if (mode === 'Delete') {
      titleStr = 'Delete Device';
    }
    if (mode === 'Edit') {
      titleStr = `Edit Device`;
    }

    return titleStr;
  }, [mode]);

  const handleCreateDeviceSubmit = async (data: DeviceCreateFormModel) => {
    if (data) {
      const deviceData = data;
      deviceData.configuration =
        minifyJson(deviceData?.configuration ?? '{}') || '';
      let devName = deviceData.host_name || '';
      if (devName.length <= 2) {
        if (Number(devName) < 10 && devName.length === 1) {
          devName = `0${devName}`;
        }
      }
      deviceData.host_name = `${deviceData.host_name_prefix}${devName}`;
      if (mode === 'Edit') {
        deviceData.device_uid = deviceId;
      }

      // for log forwarder devices we need to set the default log sources
      if (mode === 'New' && deviceData.device_type_id === 2) {
        deviceData.default_log_sources = DEFAULT_LOG_SOURCES_FOR_NEW_DEVICE;
      }

      mutation.mutate(deviceData as DeviceCreateDTO);
    }
  };
  const handleError = () => {
    // intentionally empty
  };

  const buttonLabel = useMemo(() => {
    if (mode === 'Edit') return 'Update';
    if (mode === 'New') return 'Create';
    return 'Submit';
  }, [mode]);

  return (
    <Dialog
      className={classes.dialogContainer}
      open={showDevicePopup}
      onClose={handleClose}
      scroll={scroll}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      fullWidth
    >
      <DialogTitle id="scroll-dialog-title">
        {title}
        {mode === 'Edit' &&
          selectedDeviceEditDetails?.devices_tf_jobs !== null && (
            <FontAwesomeIcon
              style={{ marginLeft: '.25rem', float: 'right' }}
              id="toggleRow3"
              icon={faRotate}
              size="sm"
              className={classes.warningColor}
            />
          )}
      </DialogTitle>

      <DialogContent>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(handleCreateDeviceSubmit, handleError)}>
            {(mode === 'New' || mode === 'Delete') &&
              selectedOrgData?.id === 0 && (
                <DialogContentText sx={{ pt: 2, pb: 1 }}>
                  <Alert severity="info">
                    <Typography>
                      Please select an organization to add or delete a device.
                    </Typography>
                  </Alert>
                </DialogContentText>
              )}

            {((selectedOrgData?.id !== 0 && mode === 'New') ||
              mode === 'Edit') && <DeviceCreateForm />}

            {selectedOrgData?.id !== 0 && mode === 'Delete' && (
              <DialogContentText>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Are you sure you want to delete the device{' '}
                  <strong>{hostName}?</strong>
                </Typography>
                <Typography variant="body1">
                  Deleting a device will also delete all sources associated with
                  this device
                </Typography>
              </DialogContentText>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </form>
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Box className={classes.footerMessage}>
          {selectedOrgData?.id === 0 && mode === 'Edit' && (
            <Typography>
              <FontAwesomeIcon
                size="lg"
                style={{ marginRight: '.5rem' }}
                icon={faTriangleExclamation}
              />
              Changes cannot be made until an organization is selected.
            </Typography>
          )}
        </Box>
        <Button
          onClick={(e: React.MouseEvent<HTMLElement>) =>
            handleClose(e, CLOSE_REASON.BUTTON)
          }
        >
          Cancel
        </Button>

        {/* Enable the action button only when an organization is selected, and the mode is valid */}
        {selectedOrgData?.id !== 0 &&
          (mode === 'New' || mode === 'Edit' || mode === 'Delete') && (
            <Button
              onClick={formMethods.handleSubmit(
                handleCreateDeviceSubmit,
                handleError
              )}
              variant="contained"
              color="primary"
              size="small"
              type="submit"
            >
              {!mutation.isPending && <span>{buttonLabel}</span>}
              {mutation.isPending && (
                <CircularProgress color="secondary" size={20} />
              )}
            </Button>
          )}
      </DialogActions>
    </Dialog>
  );
};
export default DeviceCreateDialog;
